import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Appearance, ColorSchemeName } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../store";
import {
  initializeSavedThemePreference,
  setThemePreference as setThemePreferenceAction,
} from "../store/slices/themeSlice";
import { lightThemeColors } from "./tokens/lightTheme";
import { darkThemeColors } from "./tokens/darkTheme";
import { ResolvedTheme, ThemeColors, ThemeMode } from "./types";
import { applyGlobalDarkModeWeb } from "./globalDarkModeWeb";

export interface ThemeContextValue {
  /** The user's raw preference: 'light' | 'dark' | 'system' */
  preference: ThemeMode;
  /** The actual appearance applied after resolving 'system' against the OS setting */
  resolvedTheme: ResolvedTheme;
  /** Semantic color tokens for the resolved theme */
  colors: ThemeColors;
  /** Update the user's theme preference (persisted automatically) */
  setThemePreference: (mode: ThemeMode) => void;
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const resolveScheme = (scheme: ColorSchemeName): ResolvedTheme =>
  scheme === "dark" ? "dark" : "light";

interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * Centralized theme provider for the Lighthouse application.
 *
 * Resolves the user's persisted preference ('light' | 'dark' | 'system')
 * against the live OS color scheme (for 'system') and exposes semantic
 * color tokens to the rest of the app via `useTheme()`.
 *
 * Must be rendered inside the Redux <Provider> (it reads/dispatches the
 * `theme` slice for persistence), and should wrap the app root so any
 * component can opt in to theming via the hook.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const preference = useSelector((state: RootState) => state.theme.preference);

  // Tracks the live OS color scheme so 'system' reacts to OS changes without a reload.
  const [systemScheme, setSystemScheme] = useState<ColorSchemeName>(
    Appearance.getColorScheme()
  );

  // Load any previously saved preference (web localStorage / native AsyncStorage)
  // on mount, mirroring the initializeSavedLocation() pattern used elsewhere.
  useEffect(() => {
    dispatch(initializeSavedThemePreference());
  }, [dispatch]);

  // Subscribe to OS-level appearance changes so 'system' mode updates live.
  useEffect(() => {
    const subscription = Appearance.addChangeListener(({ colorScheme }) => {
      setSystemScheme(colorScheme);
    });
    return () => {
      subscription?.remove?.();
    };
  }, []);

  const resolvedTheme: ResolvedTheme =
    preference === "system" ? resolveScheme(systemScheme) : preference;

  const colors: ThemeColors = resolvedTheme === "dark" ? darkThemeColors : lightThemeColors;

  // Global web-only retrofit: instantly re-colors every un-migrated
  // StyleSheet.create()-based component's common surface colors when
  // Dark/Light changes, without touching those component files. No-op on
  // native. See globalDarkModeWeb.ts for full rationale/limitations.
  //
  // Lazily-loaded (React.lazy) screen chunks that insert new
  // StyleSheet.create() rules *after* this effect last ran are now caught
  // INSTANTLY: applyGlobalDarkModeWeb patches CSSStyleSheet.insertRule once
  // so every newly-inserted rule is swept synchronously, immediately after
  // insertion (see globalDarkModeWeb.ts). The interval below is only a
  // defensive backstop for any edge case that bypasses that hook — it is
  // deliberately infrequent since it is no longer the primary mechanism
  // (an earlier, much shorter 1.5s-only polling interval was the root
  // cause of a visible "flash" of un-swept Light colors during navigation).
  useEffect(() => {
    applyGlobalDarkModeWeb(resolvedTheme);

    if (resolvedTheme !== "dark") return;

    const intervalId = setInterval(() => {
      applyGlobalDarkModeWeb(resolvedTheme);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [resolvedTheme]);

  const handleSetThemePreference = useCallback(
    (mode: ThemeMode) => {
      dispatch(setThemePreferenceAction(mode));
    },
    [dispatch]
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      resolvedTheme,
      colors,
      setThemePreference: handleSetThemePreference,
    }),
    [preference, resolvedTheme, colors, handleSetThemePreference]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/**
 * Internal accessor for the theme context. Use the `useTheme` hook
 * (src/theme/useTheme.ts) from application code instead of this directly.
 */
export const useThemeContext = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeContext must be used within a <ThemeProvider>");
  }
  return context;
};
